import { api } from './api';

// Interfaces pour les données d'inscription
export interface SignupData {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  password: string;
  role?: string;
}

export interface LoginData {
  email: string;
  password: string;
}

export interface UserProfile {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface User {
  _id?: string;
  id?: number;
  email: string;
  nom?: string;
  prenom?: string;
  nom_entreprise?: string;
  account_type?: 'individual' | 'company';
  is_active?: boolean;
  /** API auth (camelCase depuis toPublicJSON) */
  twoFactorEnabled?: boolean;
  role: 'user' | 'admin' | string;
  createdAt?: string;
  updatedAt?: string;
  created_at?: string;
  updated_at?: string;
  first_name?: string;
  last_name?: string;
  firstName?: string;
  lastName?: string;
  email_verified?: boolean;
  [key: string]: unknown;
}

export interface UserAddress {
  street: string;
  city: string;
  postalCode: string;
  country: string;
}

/** Réponse login : soit session complète, soit étape 2FA */
export type LoginResponseData =
  | {
      needsTwoFactor: true;
      twoFactorToken: string;
    }
  | {
      user: User;
      tokens: {
        accessToken?: string;
        access?: string;
        token?: string;
        expiresIn?: string;
        [key: string]: unknown;
      };
    };

// Interfaces de réponse
export interface LoginResponse {
  success: boolean;
  message?: string;
  data?: LoginResponseData;
}

export interface ProfileResponse {
  success: boolean;
  data?: { user: User };
  user?: User;
  message?: string;
}

export interface SignupResponse {
  success: boolean;
  token?: string;
  user?: User;
  message?: string;
  /** Réponse API signup (tokens + user imbriqués) */
  data?: {
    tokens?: { accessToken?: string; access?: string };
    user?: User;
  };
}

/** Erreur API structurée (validation par champ, codes métier) */
export class AuthApiError extends Error {
  readonly code?: string
  readonly fieldErrors: Record<string, string>
  readonly status?: number

  constructor(
    message: string,
    options?: { code?: string; fieldErrors?: Record<string, string>; status?: number }
  ) {
    super(message)
    this.name = 'AuthApiError'
    this.code = options?.code
    this.fieldErrors = options?.fieldErrors ?? {}
    this.status = options?.status
  }
}

function parseValidationFieldErrors(errors: unknown): Record<string, string> {
  const out: Record<string, string> = {}
  if (!Array.isArray(errors)) return out
  for (const e of errors) {
    const row = e as { path?: string; param?: string; msg?: string; message?: string }
    const key = row.path ?? row.param
    if (typeof key === 'string' && key && !out[key]) {
      out[key] = String(row.msg ?? row.message ?? '')
    }
  }
  return out
}

// Service d'authentification
export const authService = {
  // Méthode d'inscription
  signup: async (userData: SignupData): Promise<SignupResponse> => {
    try {
      const response = await api.post('/api/auth/signup', userData)
      return response.data
    } catch (error: any) {
      const response = error.response ?? error.originalError?.response
      const errorData = response?.data as
        | { message?: string; code?: string; errors?: unknown }
        | undefined
      const status = response?.status as number | undefined

      if (!response) {
        const msg =
          typeof error.message === 'string' && error.message.length > 0
            ? error.message
            : 'Une erreur est survenue. Veuillez réessayer.'
        const isNetwork =
          /connexion|r[ée]seau|network|serveur|server|timeout/i.test(msg) ||
          error.code === 'ECONNABORTED'
        throw new AuthApiError(msg, { code: isNetwork ? 'NETWORK' : 'UNKNOWN' })
      }

      if (status === 429) {
        throw new AuthApiError(errorData?.message || 'Trop de tentatives', {
          code: 'RATE_LIMIT',
          status
        })
      }

      const list = errorData?.errors
      if (Array.isArray(list) && list.length > 0) {
        const fieldErrors = parseValidationFieldErrors(list)
        throw new AuthApiError(errorData?.message || 'Données invalides', {
          code: (errorData?.code as string) || 'VALIDATION_ERROR',
          fieldErrors,
          status
        })
      }

      if (status === 409) {
        const apiMsg = errorData?.message || 'Cet email est déjà utilisé'
        throw new AuthApiError(apiMsg, {
          code: (errorData?.code as string) || 'EMAIL_ALREADY_EXISTS',
          fieldErrors: { email: apiMsg },
          status
        })
      }

      if (status != null && status >= 500) {
        throw new AuthApiError(
          error.message || 'Une erreur serveur est survenue. Veuillez réessayer plus tard.',
          { code: 'SERVER_ERROR', status }
        )
      }

      throw new AuthApiError(errorData?.message || error.message || 'Erreur lors de l\'inscription', {
        code: errorData?.code as string | undefined,
        status
      })
    }
  },

  /** Finalise la connexion après saisie du code TOTP */
  loginTwoFactor: async (
    twoFactorToken: string,
    code: string
  ): Promise<LoginResponse> => {
    try {
      const response = await api.post('/api/auth/login/2fa', {
        twoFactorToken,
        code: String(code).replace(/\s/g, '')
      });
      return response.data;
    } catch (error: any) {
      if (error.message) {
        throw new Error(error.message);
      }
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Une erreur est survenue. Veuillez réessayer.');
    }
  },

  /** Prépare l’activation 2FA (secret + QR) */
  twoFactorSetup: async (): Promise<{
    success: boolean;
    data: { secret: string; otpauthUrl: string; qrDataUrl: string };
  }> => {
    const response = await api.post('/api/auth/2fa/setup');
    return response.data;
  },

  twoFactorEnable: async (
    secret: string,
    code: string
  ): Promise<{ success: boolean; message?: string; data?: { user: User } }> => {
    try {
      const response = await api.post('/api/auth/2fa/enable', {
        secret,
        code: String(code).replace(/\s/g, '')
      });
      return response.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error(error.message || 'Erreur lors de l’activation 2FA');
    }
  },

  twoFactorDisable: async (
    password: string,
    code: string
  ): Promise<{ success: boolean; message?: string; data?: { user: User } }> => {
    try {
      const response = await api.post('/api/auth/2fa/disable', {
        password,
        code: String(code).replace(/\s/g, '')
      });
      return response.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error(error.message || 'Erreur lors de la désactivation 2FA');
    }
  },

  // Méthode de connexion
  login: async (email: string, password: string): Promise<LoginResponse> => {
    try {
      const response = await api.post('/api/auth/login', {
        email,
        password,
      });
      return response.data;
    } catch (error: any) {
      // L'intercepteur api.ts transforme déjà l'erreur avec un message
      // Vérifier d'abord si le message existe directement
      if (error.message) {
        throw new Error(error.message);
      }
      // Sinon, essayer de récupérer depuis error.response.data
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      // Sinon, essayer depuis error.originalError
      if (error.originalError?.response?.data?.message) {
        throw new Error(error.originalError.response.data.message);
      }
      // Erreurs réseau
      if (error.request) {
        throw new Error('Impossible de contacter le serveur. Veuillez vérifier votre connexion.');
      }
      // Erreur générique
      throw new Error('Une erreur est survenue. Veuillez réessayer.');
    }
  },

  // Méthode pour récupérer les informations du profil utilisateur
  getCurrentUser: async (): Promise<ProfileResponse> => {
    try {
      const response = await api.get('/api/auth/profile');
      return response.data;
    } catch (error: any) {
      if (error.response) {
        throw new Error(error.response.data.message || 'Erreur lors de la récupération du profil');
      } else if (error.request) {
        throw new Error('Impossible de contacter le serveur. Veuillez vérifier votre connexion.');
      } else {
        throw new Error('Une erreur est survenue. Veuillez réessayer.');
      }
    }
  },

  // Méthode pour mettre à jour le profil utilisateur
  updateUserProfile: async (profileData: Partial<UserProfile>): Promise<UserProfile> => {
    try {
      const response = await api.put('/api/users/profile', profileData);
      return response.data.user;
    } catch (error: any) {
      if (error.response) {
        throw new Error(error.response.data.message || 'Erreur lors de la mise à jour du profil');
      } else if (error.request) {
        throw new Error('Impossible de contacter le serveur. Veuillez vérifier votre connexion.');
      } else {
        throw new Error('Une erreur est survenue. Veuillez réessayer.');
      }
    }
  },

  // Méthode pour changer le mot de passe
  changePassword: async (currentPassword: string, newPassword: string): Promise<void> => {
    try {
      await api.put('/api/users/password/update', { currentPassword, newPassword });
    } catch (error: any) {
      if (error.response) {
        throw new Error(error.response.data.message || 'Erreur lors du changement de mot de passe');
      } else if (error.request) {
        throw new Error('Impossible de contacter le serveur. Veuillez vérifier votre connexion.');
      } else {
        throw new Error('Une erreur est survenue. Veuillez réessayer.');
      }
    }
  },

  // Méthode pour déconnexion côté serveur
  logout: async (): Promise<void> => {
    try {
      // Appeler l'endpoint de déconnexion côté serveur
      await api.post('/api/auth/logout');
    } catch (error: any) {
      // Ne pas faire échouer la déconnexion si l'API échoue
      console.warn('Erreur lors de la déconnexion côté serveur:', error);
    } finally {
      // Toujours nettoyer le localStorage côté client
      localStorage.removeItem('token');
      localStorage.removeItem('user-data');
      localStorage.removeItem('last-activity');
      localStorage.removeItem('user-role');
    }
  },

  // Méthode pour vérifier la validité d'un token
  validateToken: async (): Promise<boolean> => {
    try {
      const response = await api.get('/api/auth/validate');
      return response.data.valid === true;
    } catch (error: any) {
      return false;
    }
  },

  // Méthode pour rafraîchir un token (si implémenté côté serveur)
  refreshToken: async (): Promise<{ success: boolean; token?: string; message?: string }> => {
    try {
      const response = await api.post('/api/auth/refresh');
      return response.data;
    } catch (error: any) {
      return { success: false, message: 'Impossible de rafraîchir le token' };
    }
  },

  // Méthode pour demander une réinitialisation de mot de passe
  forgotPassword: async (email: string): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await api.post('/api/auth/forgot-password', { email });
      return response.data;
    } catch (error: any) {
      if (error.response) {
        throw new Error(error.response.data.message || 'Erreur lors de la demande de réinitialisation');
      } else if (error.request) {
        throw new Error('Impossible de contacter le serveur. Veuillez vérifier votre connexion.');
      } else {
        throw new Error('Une erreur est survenue. Veuillez réessayer.');
      }
    }
  },

  // Méthode pour réinitialiser le mot de passe avec un token
  resetPassword: async (token: string, password: string): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await api.post('/api/auth/reset-password', { token, password });
      return response.data;
    } catch (error: any) {
      if (error.response) {
        throw new Error(error.response.data.message || 'Erreur lors de la réinitialisation du mot de passe');
      } else if (error.request) {
        throw new Error('Impossible de contacter le serveur. Veuillez vérifier votre connexion.');
      } else {
        throw new Error('Une erreur est survenue. Veuillez réessayer.');
      }
    }
  },

  /** Confirme l’email avec le jeton reçu par lien (page /auth/verify-email). */
  verifyEmailWithToken: async (
    token: string
  ): Promise<{ success: boolean; message?: string; data?: { user: User } }> => {
    try {
      const response = await api.post('/api/auth/verify-email', { token });
      return response.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      if (error.message) {
        throw new Error(error.message);
      }
      throw new Error('Une erreur est survenue lors de la vérification.');
    }
  },

  /** Renvoie un email de validation (compte connecté, email non vérifié). */
  resendVerificationEmail: async (): Promise<{ success: boolean; message?: string }> => {
    try {
      const response = await api.post('/api/auth/verify-email/resend');
      return response.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      if (error.message) {
        throw new Error(error.message);
      }
      throw new Error('Impossible de renvoyer l’email pour le moment.');
    }
  }
};
